import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import { checkOutClinicAssistant } from '../CheckIn/clinicAssistantServices'
import { toast } from 'react-toastify'
import PersonIcon from '@mui/icons-material/Person'
import { 
  Button, 
  Drawer, 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemText, 
  ListItemIcon,
  Divider,
  Chip,
  IconButton,
  Avatar,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import dayjs from 'dayjs'
import { useContext, useEffect, useRef, useState, useMemo, memo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
  addServiceProviderToData,
  selectServiceProvidersData,
} from 'src/clinic/reducers/serviceProviders/serviceProvidersReducer'
import { getCPDataWithoutParams } from 'src/clinic/services'
import { StyledCustomSelect } from 'src/consumer/components/helperComponentsStyles'
import { ROLE_CA_FLOWS } from 'src/consumer/constants'
import { getTimezoneOffset } from 'src/consumer/helpers/utilFunctions'
import { appVersion, platformWeb } from 'src/consumer/services'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import { DocSideContext } from 'src/context/DocSideContextProvider'
import useElementDimensions from 'src/customHooks/customHooks'
import { PageHeadlineComponent } from 'src/mentor/components/MentorAvailabilityPage/MentorAvailabilityPageStyles'
import HeaderItems from 'src/mentor/components/MentorClinicBookingsPage/component/HeaderItems'
import { MENTOR_CLINIC_BOOKING_TABS } from 'src/mentor/components/MentorClinicBookingsPage/util'
import { getActiveMentorsInAClinicLocation } from '../ClinicCounterScreen/utils'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { useBodyScrollLock } from 'src/hooks/useBodyScrollLock'
import PatientSearchDrawer from 'src/mentor/components/MentorClinicBookingsPage/component/PatientSearchDrawer'
import { useProfileDrawer } from '../ClinicAssistantProfileDrawer/withProfileDrawer'
import ClinicAssistantProfileDrawer from '../ClinicAssistantProfileDrawer/index'
import {
  AvailabilityPageContainer,
  ClinicManageBookingsContainer,
} from './ClinicManageBookingsPageStyles'
import DayViewCalendarComponent from './DayViewCalendarComponent'
import { PatientListingPageComponent } from 'src/clinic/views/patientListing/PatientListingPage'

// Memoized Avatar component to prevent unnecessary re-renders
// Using React.memo with strict comparison - simplified to match stable floating header version
const PractitionerAvatar = memo(({ imageUrl, managerId }) => {
  return (
    <Avatar
      src={imageUrl || undefined}
      sx={{
        width: 32,
        height: 32,
        bgcolor: 'rgba(116, 80, 170, 0.1)',
        color: 'rgba(116, 80, 170, 0.9)',
        border: '2px solid rgba(116, 80, 170, 0.2)',
        flexShrink: 0,
      }}
      imgProps={{
        style: {
          objectFit: 'cover',
          width: '100%',
          height: '100%',
        },
        onError: (e) => {
          // Prevent further error events and infinite retries
          e.target.onerror = null
          // Remove the src to prevent repeated load attempts
          e.target.src = ''
        },
        loading: 'lazy', // Use lazy loading to prevent aggressive fetching
        decoding: 'async', // Use async decoding to prevent blocking
      }}
    >
      <PersonIcon sx={{ fontSize: 18 }} />
    </Avatar>
  )
}, (prevProps, nextProps) => {
  // Strict comparison: only re-render if imageUrl or managerId actually changed
  // Return true if props are equal (don't re-render), false if different (re-render)
  return (
    prevProps.imageUrl === nextProps.imageUrl &&
    prevProps.managerId === nextProps.managerId
  )
})

PractitionerAvatar.displayName = 'PractitionerAvatar'

// Memoized Practitioner Button to prevent re-renders
const PractitionerButton = memo(({ 
  avatarElement, 
  buttonText, 
  onClick,
  isActive = false
}) => {
  return (
    <Button
      variant="outlined"
      startIcon={
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          {avatarElement}
          {isActive && (
            <Chip
              label="✓"
              size="small"
              sx={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                height: '18px',
                minWidth: '18px',
                fontSize: '10px',
                fontWeight: 700,
                backgroundColor: '#10b981',
                color: 'white',
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                zIndex: 1,
                '& .MuiChip-label': {
                  padding: '0 2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
              }}
            />
          )}
        </Box>
      }
      onClick={onClick}
      sx={{
        minWidth: '200px',
        justifyContent: 'flex-start',
        textTransform: 'none',
        borderColor: isActive 
          ? 'rgba(16, 185, 129, 0.5)' 
          : 'rgba(116, 80, 170, 0.3)',
        color: 'rgba(116, 80, 170, 0.9)',
        backgroundColor: isActive 
          ? 'rgba(16, 185, 129, 0.05)' 
          : 'transparent',
        '&:hover': {
          borderColor: isActive 
            ? 'rgba(16, 185, 129, 0.7)' 
            : 'rgba(116, 80, 170, 0.5)',
          backgroundColor: isActive 
            ? 'rgba(16, 185, 129, 0.08)' 
            : 'rgba(116, 80, 170, 0.05)',
        },
        '& .MuiButton-startIcon': {
          marginRight: '12px',
        },
      }}
    >
      {buttonText}
    </Button>
  )
}, (prevProps, nextProps) => {
  // Only re-render if props actually changed
  return (
    prevProps.avatarElement === nextProps.avatarElement &&
    prevProps.buttonText === nextProps.buttonText &&
    prevProps.onClick === nextProps.onClick &&
    prevProps.isActive === nextProps.isActive
  )
})

PractitionerButton.displayName = 'PractitionerButton'

// Custom Option component for manager selection
const ManagerCustomOption = (props) => {
  const { data } = props

  // Determine role based on categories
  let role = ''
  if (data?.categories?.includes('doctor')) {
    role = 'Doctor'
  } else if (data?.categories?.includes('therapist')) {
    role = 'Therapist'
  }

  return (
    <div
      {...props.innerProps}
      style={{
        padding: '8px 12px',
        cursor: 'pointer',
        backgroundColor: props.isFocused ? '#f5f5f5' : 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <div>
        <div style={{ fontWeight: '500', fontSize: '14px' }}>{data.name}</div>
        {role && <div style={{ fontSize: '12px', color: '#666' }}>{role}</div>}
      </div>
      {data?.isActive && (
        <div
          style={{
            background: 'green',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: '500',
            marginLeft: '8px',
          }}
        >
          Checked In
        </div>
      )}
    </div>
  )
}

export default function ClinicManageBookingsPage() {
  const { clinicBrandId, clinicId } = useParams()
  const { assistantData } = useContext(ClinicDataContext)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const serviceProviders = useSelector(selectServiceProvidersData)
  const { setFlowId } = useContext(DocSideContext)
  const [selectedManagerId, setSelectedManagerId] = useState()
  const [managersProfileData, setManagersProfileData] = useState([])
  
  // Memoize the specific manager from Redux to prevent unnecessary recalculations
  // Use refs to track previous values and only update when actual data changes
  const selectedManagerFromReduxRef = useRef(null)
  const lastReduxManagerIdRef = useRef(null)
  const lastReduxManagerProfilePicIdRef = useRef(null)
  
  const selectedManagerFromRedux = useMemo(() => {
    if (!selectedManagerId || !serviceProviders?.length) {
      selectedManagerFromReduxRef.current = null
      lastReduxManagerIdRef.current = null
      lastReduxManagerProfilePicIdRef.current = null
      return null
    }
    
    const found = serviceProviders.find(sp => sp?.id === selectedManagerId)
    
    if (!found) {
      selectedManagerFromReduxRef.current = null
      lastReduxManagerIdRef.current = null
      lastReduxManagerProfilePicIdRef.current = null
      return null
    }
    
    // Only update if manager ID changed or profilePicId changed (indicates actual data change)
    if (
      selectedManagerId === lastReduxManagerIdRef.current &&
      found.profilePicId === lastReduxManagerProfilePicIdRef.current &&
      selectedManagerFromReduxRef.current !== null
    ) {
      return selectedManagerFromReduxRef.current
    }
    
    // Update refs and return new reference
    selectedManagerFromReduxRef.current = found
    lastReduxManagerIdRef.current = selectedManagerId
    lastReduxManagerProfilePicIdRef.current = found.profilePicId
    
    return found
  }, [serviceProviders, selectedManagerId])
  const [selectedTab, setSelectedTab] = useState(MENTOR_CLINIC_BOOKING_TABS.calendar.id)
  const [activeMentorsInThisClinicLocationMap, setActiveMentorsInThisClinicLocationMap] = useState(
    {},
  )
  const [practitionerDrawerOpen, setPractitionerDrawerOpen] = useState(false)
  const [isPatientSearchOpen, setIsPatientSearchOpen] = useState(false)
  const profileDrawer = useProfileDrawer()

  const { ref: headerRef, dimensions } = useElementDimensions()
  const dayViewCalendarRef = useRef(null)

  async function getAndSetActiveMentorsInAClinicLocation() {
    const responseMap = await getActiveMentorsInAClinicLocation({ clinicLocationId: clinicId })
    // Only update state if data actually changed to prevent unnecessary re-renders
    setActiveMentorsInThisClinicLocationMap((prev) => {
      // Compare the maps by converting to JSON strings
      const prevStr = JSON.stringify(prev)
      const newStr = JSON.stringify(responseMap)
      if (prevStr === newStr) {
        // Data is the same, return previous reference to prevent re-render
        return prev
      }
      // Data changed, return new map
      return responseMap
    })
  }

  const [loadingObject, setLoadingObject] = useState({
    mentorProfileDataLoading: false,
    managersLoading: false,
  })

  const assistantProfileForThisClinic = assistantData?.clinicAssistantProfileData?.find(
    (item) => item?.clinicId === clinicId,
  )

  // Create clinic assistant profile data for header display
  const clinicAssistantProfileData = useMemo(() => {
    if (!assistantData?.userProfileData) return null
    return {
      name: assistantData.userProfileData.name || assistantData.userProfileData.firstName,
      firstName: assistantData.userProfileData.firstName,
      // Add other fields as needed
    }
  }, [assistantData?.userProfileData])

  // Memoize validManagers to prevent unnecessary recalculations
  const validManagers = useMemo(() => {
    if (!assistantProfileForThisClinic?.supportRoles) return []
    return assistantProfileForThisClinic.supportRoles.map((supportRole) => {
      const mentor = managersProfileData?.find((item) => item?.id === supportRole?.managerId)
      const isActive = Object.values(activeMentorsInThisClinicLocationMap || {}).find(
        (manager) => manager?.id === supportRole?.managerId,
      )
      return {
        ...mentor,
        isActive,
      }
    })
  }, [assistantProfileForThisClinic?.supportRoles, managersProfileData, activeMentorsInThisClinicLocationMap])

  // Group managers by their categories and sort checked-in to top
  const groupedValidManagers = () => {
    if (!validManagers || validManagers.length === 0) return []

    // Sort function: checked-in first, then alphabetically
    const sortManagers = (a, b) => {
      // Checked-in practitioners come first
      if (a?.isActive && !b?.isActive) return -1
      if (!a?.isActive && b?.isActive) return 1
      // Then sort alphabetically
      return (a?.name || '').localeCompare(b?.name || '')
    }

    const doctors = validManagers
      .filter((manager) => manager?.categories?.includes('doctor'))
      .sort(sortManagers)

    const therapists = validManagers
      .filter((manager) => manager?.categories?.includes('therapist'))
      .sort(sortManagers)

    const others = validManagers
      .filter(
        (manager) =>
          !manager?.categories?.includes('doctor') && !manager?.categories?.includes('therapist'),
      )
      .sort(sortManagers)

    const groups = []

    if (doctors.length > 0) {
      groups.push({
        label: 'Doctors',
        options: doctors,
      })
    }

    if (therapists.length > 0) {
      groups.push({
        label: 'Therapists',
        options: therapists,
      })
    }

    if (others.length > 0) {
      groups.push({
        label: 'Others',
        options: others,
      })
    }

    return groups
  }

  // Memoize thisClinicData to prevent unnecessary recalculations
  const thisClinicData = useMemo(() => {
    return assistantData?.associatedClinicsData?.find((item) => item.id === clinicId)
  }, [assistantData?.associatedClinicsData, clinicId])

  // Memoize clinicBrandId to prevent HeaderItems re-renders
  const memoizedClinicBrandId = useMemo(() => {
    return thisClinicData?.clinicBrandId
  }, [thisClinicData?.clinicBrandId])

  // Memoize clinicAssociationData to prevent HeaderItems re-renders
  const memoizedClinicAssociationData = useMemo(() => {
    return assistantData?.associatedClinicsData
  }, [assistantData?.associatedClinicsData])

  // Memoize hidePatientHistoryRelatedTabs to prevent HeaderItems re-renders
  const memoizedHidePatientHistoryRelatedTabs = useMemo(() => {
    return !assistantProfileForThisClinic?.canViewPatientHistory
  }, [assistantProfileForThisClinic?.canViewPatientHistory])

  async function getAndSetRoleAssistantData() {
    setSelectedManagerId(assistantProfileForThisClinic?.supportRoles?.[0]?.managerId)
  }

  // Memoize refreshData to prevent HeaderItems re-renders
  const refreshData = useCallback(async () => {
    if (!dayViewCalendarRef.current) {
      return
    }
    await dayViewCalendarRef.current?.getAndSetBookedSlotsOfSelectedDate()
  }, [])

  useEffect(() => {
    getAndSetRoleAssistantData()
  }, [assistantData])

  async function getAndSetManagersProfileData() {
    setLoadingObject((prev) => ({ ...prev, managersLoading: true }))
    if (selectedManagerId) {
      if (managersProfileData.length === 0) {
        if (Array.isArray(assistantProfileForThisClinic?.supportRoles)) {
          setLoadingObject((prev) => ({ ...prev, mentorProfileDataLoading: true }))
          
          // Use Promise.all with map instead of forEach to batch all updates
          const managersDataPromises = assistantProfileForThisClinic.supportRoles.map(async (supportRole) => {
            // Check if service provider data is available in redux
            const foundSvcProviderInRedux = serviceProviders?.find(
              (item) => item?.id === supportRole?.managerId,
            )
            if (Boolean(foundSvcProviderInRedux)) {
              return { data: foundSvcProviderInRedux, shouldDispatch: false }
            } else {
              // Get from API (don't dispatch yet - batch dispatches after all promises resolve)
              // Get timezone offset, default to IST (+05:30) if unavailable
              let timezoneOffset = '+05:30' // Default to IST
              try {
                // Ensure getTimezoneOffset is available and callable
                if (getTimezoneOffset && typeof getTimezoneOffset === 'function') {
                  const offset = getTimezoneOffset()
                  if (offset && typeof offset === 'string') {
                    timezoneOffset = offset
                  }
                }
              } catch (error) {
                console.error('Error getting timezone offset, using default IST (+05:30):', error)
                // Keep default value
              }
              const response = await getCPDataWithoutParams(
                `/api/secure/clinic-assistant/service-provider?id=${
                  supportRole?.managerId
                }&slug=&zoneOffset=${encodeURIComponent(
                  timezoneOffset,
                )}&appVersion=${appVersion}&platformType=${platformWeb}`,
              )
              if (response.status === 200) {
                return { data: response?.data, shouldDispatch: true }
              }
              return null
            }
          })
          
          // Wait for all promises and batch Redux dispatches
          const managersDataResults = await Promise.all(managersDataPromises)
          const validManagersData = managersDataResults
            .filter(Boolean)
            .map(result => result.data)
          
          // Batch all Redux dispatches at once to reduce serialization overhead
          managersDataResults
            .filter(result => result?.shouldDispatch && result?.data)
            .forEach(result => {
              dispatch(addServiceProviderToData(result.data))
            })
          
          // Only update state if data actually changed (prevent unnecessary re-renders)
          setManagersProfileData((prev) => {
            // Check if the data is actually different
            if (prev.length === validManagersData.length) {
              const prevIds = prev.map(m => m?.id).sort().join(',')
              const newIds = validManagersData.map(m => m?.id).sort().join(',')
              if (prevIds === newIds) {
                // Data is the same, return previous reference to prevent re-render
                return prev
              }
            }
            // Data changed, return new array
            return validManagersData
          })
          setLoadingObject((prev) => ({ ...prev, mentorProfileDataLoading: false }))
        }
      }
    }
    setLoadingObject((prev) => ({ ...prev, managersLoading: false }))
  }

  useEffect(() => {
    getAndSetRoleAssistantData()
  }, [])

  useEffect(() => {
    getAndSetManagersProfileData()
  }, [selectedManagerId])

  // Refresh calendar when practitioner changes
  useEffect(() => {
    if (selectedManagerId && dayViewCalendarRef.current) {
      // Small delay to ensure state is updated
      setTimeout(() => {
        refreshData()
      }, 200)
    }
  }, [selectedManagerId])

  // Store selected manager in a ref to maintain stable reference
  const selectedManagerDataRef = useRef(null)
  const lastManagerIdRef = useRef(null)
  
  // Memoize selected manager profile data with stable reference
  // Also check Redux store as fallback to get manager data immediately
  // Use refs to track data sources and prevent unnecessary recalculations
  const lastManagersLengthRef = useRef(0)
  const lastReduxManagerRef = useRef(null)
  
  const selectedManagersProfileData = useMemo(() => {
    if (!selectedManagerId) {
      selectedManagerDataRef.current = null
      lastManagerIdRef.current = null
      return null
    }
    
    // Quick check: if manager ID is same and we have cached data, verify it's still valid
    const managersLength = managersProfileData?.length || 0
    
    if (
      selectedManagerId === lastManagerIdRef.current &&
      managersLength === lastManagersLengthRef.current &&
      selectedManagerDataRef.current !== null
    ) {
      // Verify the cached manager still exists
      const stillExistsInManagers = managersProfileData?.find((item) => item?.id === selectedManagerId)
      const stillExists = stillExistsInManagers || selectedManagerFromRedux
      
      if (stillExists) {
        // Use the one from managersProfileData if available, otherwise from Redux
        const currentData = stillExistsInManagers || selectedManagerFromRedux
        // Only return cached if profilePicId hasn't changed (indicates same data)
        if (currentData && currentData.profilePicId === selectedManagerDataRef.current.profilePicId) {
          return selectedManagerDataRef.current
        }
      }
    }
    
    // Find the manager - check managersProfileData first, then Redux
    let found = managersProfileData?.find((item) => item?.id === selectedManagerId)
    if (!found && selectedManagerFromRedux) {
      found = selectedManagerFromRedux
    }
    
    if (found) {
      // Cache the found manager and update refs
      selectedManagerDataRef.current = found
      lastManagerIdRef.current = selectedManagerId
      lastManagersLengthRef.current = managersLength
      lastReduxManagerRef.current = selectedManagerFromRedux
      return found
    }
    
    selectedManagerDataRef.current = null
    lastManagerIdRef.current = null
    lastManagersLengthRef.current = managersLength
    lastReduxManagerRef.current = selectedManagerFromRedux
    return null
  }, [managersProfileData, selectedManagerId, selectedManagerFromRedux])

  // Use a ref to store the stable image URL to prevent blinking and infinite loops
  const imageUrlRef = useRef(null)
  const lastProfilePicIdRef = useRef(null)
  const lastCoverIconRef = useRef(null)
  
  // Memoize the image URL with a stable reference - only recalculate when profilePicId or coverIcon actually changes
  // Use selectedManagersProfileData which already has fallback logic to Redux
  const selectedManagerImageUrl = useMemo(() => {
    if (!selectedManagerId || !selectedManagersProfileData) {
      // If we have a cached URL for this manager, keep it (don't clear on every render)
      if (selectedManagerId === lastManagerIdRef.current && imageUrlRef.current !== null) {
        return imageUrlRef.current
      }
      imageUrlRef.current = null
      lastProfilePicIdRef.current = null
      lastCoverIconRef.current = null
      return null
    }
    
    const profilePicId = selectedManagersProfileData.profilePicId
    const coverIcon = selectedManagersProfileData.coverIcon
    
    // If the manager ID and image IDs haven't changed, return the cached URL
    // Reuse lastManagerIdRef from selectedManagersProfileData useMemo above
    if (
      selectedManagerId === lastManagerIdRef.current &&
      profilePicId === lastProfilePicIdRef.current &&
      coverIcon === lastCoverIconRef.current &&
      imageUrlRef.current !== null
    ) {
      return imageUrlRef.current
    }
    
    // Update refs (lastManagerIdRef is already updated in selectedManagersProfileData useMemo)
    lastProfilePicIdRef.current = profilePicId
    lastCoverIconRef.current = coverIcon
    
    if (!profilePicId && !coverIcon) {
      imageUrlRef.current = null
      return null
    }
    
    const imageUrl = getImageLink(profilePicId, coverIcon, false)
    // Normalize the URL to ensure it's always the same string reference for the same input
    const stableUrl = imageUrl ? String(imageUrl).trim() : null
    
    // Cache the URL
    imageUrlRef.current = stableUrl
    
    return stableUrl
  }, [
    selectedManagerId,
    selectedManagersProfileData?.profilePicId, 
    selectedManagersProfileData?.coverIcon
  ])

  const canPersonPrintPrescription = assistantProfileForThisClinic?.supportRoles?.find(
    (item) => item?.managerId === selectedManagerId,
  )?.canPrintPrescription

  // Memoize button text to prevent re-renders - use ref to ensure stable reference
  const buttonTextRef = useRef('Select Practitioner')
  const lastNameRef = useRef(null)
  const lastNameFirstNameRef = useRef(null)
  
  // Use refs to track manager data sources to prevent unnecessary recalculations
  const managerNameFromDataRef = useRef({ name: null, firstName: null, managerId: null })
  
  const practitionerButtonText = useMemo(() => {
    // Get name from selectedManagersProfileData (which already has fallback to Redux)
    const name = selectedManagersProfileData?.name
    const firstName = selectedManagersProfileData?.firstName
    const displayText = name || firstName || 'Select Practitioner'
    
    // Check if the name actually changed for this manager
    if (
      managerNameFromDataRef.current.managerId === selectedManagerId &&
      managerNameFromDataRef.current.name === name &&
      managerNameFromDataRef.current.firstName === firstName &&
      buttonTextRef.current === displayText
    ) {
      return buttonTextRef.current
    }
    
    // Update refs
    managerNameFromDataRef.current = { name, firstName, managerId: selectedManagerId }
    lastNameRef.current = name
    lastNameFirstNameRef.current = firstName
    buttonTextRef.current = displayText
    
    return displayText
  }, [
    selectedManagersProfileData?.name, 
    selectedManagersProfileData?.firstName,
    selectedManagerId
  ])

  // Memoize onClick handler to prevent button re-renders
  const handlePractitionerButtonClick = useCallback(() => {
    setPractitionerDrawerOpen(true)
  }, [])

  // Memoize patient search handler to prevent HeaderItems re-renders
  const handleOpenPatientSearch = useCallback(() => {
    setIsPatientSearchOpen(true)
  }, [])

  // Stabilize mentorProfileData prop to prevent HeaderItems re-renders
  // Use ref-based caching to prevent object recreation when data hasn't changed
  const headerMentorProfileDataRef = useRef(null)
  const lastHeaderMentorProfileDataIdRef = useRef(null)
  
  const headerMentorProfileData = useMemo(() => {
    const data = clinicAssistantProfileData || selectedManagersProfileData
    if (!data) {
      headerMentorProfileDataRef.current = null
      lastHeaderMentorProfileDataIdRef.current = null
      return null
    }
    
    // Use a stable identifier (id or name) to detect actual changes
    const dataId = data.id || data.name || data.firstName
    
    // If data ID hasn't changed, return cached object
    if (
      dataId === lastHeaderMentorProfileDataIdRef.current &&
      headerMentorProfileDataRef.current !== null
    ) {
      return headerMentorProfileDataRef.current
    }
    
    // Update cache
    headerMentorProfileDataRef.current = data
    lastHeaderMentorProfileDataIdRef.current = dataId
    
    return data
  }, [clinicAssistantProfileData, selectedManagersProfileData])

  // Memoize profile picture URLs for all managers to prevent infinite fetch loops
  // This ensures the Avatar src prop remains stable unless the actual profilePicId or coverIcon changes
  // Use a ref to cache the previous map to prevent unnecessary recalculations
  const managerProfilePicUrlsRef = useRef(new Map())
  const lastManagersDataHashRef = useRef('')
  
  const managerProfilePicUrls = useMemo(() => {
    // Create a hash of managers data to detect actual changes
    const managersHash = managersProfileData
      ?.map(m => `${m?.id}-${m?.profilePicId || ''}-${m?.coverIcon || ''}`)
      .sort()
      .join('|') || ''
    
    // If the data hasn't changed, return the cached map
    if (managersHash === lastManagersDataHashRef.current && managerProfilePicUrlsRef.current.size > 0) {
      return managerProfilePicUrlsRef.current
    }
    
    // Data changed, rebuild the map
    const urlMap = new Map()
    if (managersProfileData && managersProfileData.length > 0) {
      managersProfileData.forEach((manager) => {
        if (manager?.id) {
          const profilePicId = manager?.profilePicId
          const coverIcon = manager?.coverIcon
          if (profilePicId || coverIcon) {
            const url = getImageLink(profilePicId, coverIcon)
            // Normalize URL to ensure consistency
            const normalizedUrl = url ? String(url).trim() : null
            urlMap.set(manager.id, normalizedUrl)
          } else {
            urlMap.set(manager.id, null)
          }
        }
      })
    }
    
    // Update cache
    lastManagersDataHashRef.current = managersHash
    managerProfilePicUrlsRef.current = urlMap
    
    return urlMap
  }, [managersProfileData])

  // State for checkout loading
  const [checkoutLoading, setCheckoutLoading] = useState(false)

  // Memoize checkout handler to prevent re-creation on every render
  const handleCheckout = useCallback(async () => {
    setCheckoutLoading(true)
    try {
      // Get checked in details from localStorage
      const checkedInDetailsStr = localStorage.getItem('checkedInDetails')
      let chamberRoomId = 'reception' // Default fallback
      
      if (checkedInDetailsStr) {
        try {
          const checkedInDetails = JSON.parse(checkedInDetailsStr)
          chamberRoomId = checkedInDetails?.chamberRoomId || 'reception'
        } catch (e) {
          console.error('Error parsing checkedInDetails:', e)
        }
      }

      // Perform checkout
      const response = await checkOutClinicAssistant(
        chamberRoomId,
        'Checkout from manage bookings page'
      )

      if (response?.status === 200) {
        // Navigate to clinic assistant landing page after successful checkout
        navigate('/ca-home')
      } else {
        // Even if checkout fails, navigate to home (user might not be checked in)
        navigate('/ca-home')
      }
    } catch (error) {
      console.error('Error during checkout:', error)
      toast.error('Error during checkout. Please try again.')
      // Navigate anyway
      navigate('/ca-home')
    } finally {
      setCheckoutLoading(false)
    }
  }, [navigate])

  // Memoize custom checkout button to prevent re-creation on every render
  // Checkout from clinic and navigate to clinic assistant home page (/ca-home)
  const customExitButton = useMemo(() => (
    <Button
      onClick={handleCheckout}
      variant="contained"
      startIcon={<ExitToAppIcon />}
      disabled={checkoutLoading}
      sx={{
        flexDirection: 'row',
        fontSize: { xs: '12px', sm: '14px' },
        fontWeight: 700,
        padding: { xs: '6px 14px', sm: '8px 20px' },
        height: 'auto',
        minWidth: { xs: '90px', sm: '110px' },
        backgroundColor: '#ef4444',
        color: 'white',
        borderRadius: '6px',
        boxShadow: { xs: '0 2px 6px rgba(239, 68, 68, 0.3)', sm: '0 2px 8px rgba(239, 68, 68, 0.3)' },
        '&:hover': {
          backgroundColor: '#dc2626',
          boxShadow: { xs: '0 4px 12px rgba(239, 68, 68, 0.4)', sm: '0 4px 12px rgba(239, 68, 68, 0.4)' },
        },
        '&:disabled': {
          backgroundColor: '#ef4444',
          opacity: 0.6,
        },
        '& .MuiButton-startIcon': {
          marginRight: '4px',
        },
      }}
    >
      {checkoutLoading ? 'Checking out...' : 'Checkout'}
    </Button>
  ), [handleCheckout, checkoutLoading])

  // Memoize patient history API signature to prevent re-creation
  const patientHistoryApiSignature = useMemo(() => {
    if (!selectedManagerId) return null
    return `/api/secure/patient/reports-for-clinic-assistant/list?practitioner-id=${selectedManagerId}`
  }, [selectedManagerId])

  // Use refs to stabilize isActive state and prevent unnecessary re-renders
  const isActiveRef = useRef(false)
  const lastActiveCheckRef = useRef({ managerId: null, activeManagerIds: null })
  const [isSelectedManagerActive, setIsSelectedManagerActive] = useState(false)
  
  // Check if selected manager is active (checked in) - only update state when value actually changes
  useEffect(() => {
    if (!selectedManagerId) {
      if (isActiveRef.current !== false) {
        isActiveRef.current = false
        setIsSelectedManagerActive(false)
      }
      return
    }
    
    // Create a stable key from active mentor IDs to detect changes
    const activeManagerIds = Object.values(activeMentorsInThisClinicLocationMap || {})
      .map(manager => manager?.id)
      .filter(Boolean)
      .sort()
      .join(',')
    
    const isActive = Boolean(
      Object.values(activeMentorsInThisClinicLocationMap || {}).find(
        (manager) => manager?.id === selectedManagerId
      )
    )
    
    // Only update state if manager ID changed, active manager list changed, or active status actually changed
    if (
      lastActiveCheckRef.current.managerId === selectedManagerId &&
      lastActiveCheckRef.current.activeManagerIds === activeManagerIds &&
      isActiveRef.current === isActive
    ) {
      // No change, don't update state
      return
    }
    
    // Update refs and state
    lastActiveCheckRef.current = { managerId: selectedManagerId, activeManagerIds }
    isActiveRef.current = isActive
    setIsSelectedManagerActive(isActive)
  }, [activeMentorsInThisClinicLocationMap, selectedManagerId])

  // Create practitionerButtonProps for both main header and floating header
  // Pass primitive props so headers can use the same SimplePractitionerButton component
  // This prevents unnecessary re-renders by avoiding React element creation
  // Use ref-based caching to prevent object recreation when values haven't changed
  const practitionerButtonPropsRef = useRef(null)
  const lastPractitionerButtonPropsRef = useRef({
    managerId: null,
    imageUrl: null,
    buttonText: null,
    isActive: null,
    role: null
  })
  
  // Determine role from selectedManagersProfileData categories
  // Also check managersProfileData and selectedManagerFromRedux as fallback
  const selectedManagerRole = useMemo(() => {
    // Try selectedManagersProfileData first
    let categories = selectedManagersProfileData?.categories
    
    // If not available, try to find from managersProfileData
    if (!categories && selectedManagerId && managersProfileData?.length > 0) {
      const managerFromList = managersProfileData.find(m => m?.id === selectedManagerId)
      categories = managerFromList?.categories
    }
    
    // If still not available, try selectedManagerFromRedux
    if (!categories && selectedManagerFromRedux) {
      categories = selectedManagerFromRedux.categories
    }
    
    if (!categories || !Array.isArray(categories)) return null
    if (categories.includes('doctor')) return 'Doctor'
    if (categories.includes('therapist')) return 'Therapist'
    return null
  }, [selectedManagersProfileData?.categories, selectedManagerId, managersProfileData, selectedManagerFromRedux])
  
  const practitionerButtonProps = useMemo(() => {
    if (!selectedManagerId) {
      practitionerButtonPropsRef.current = null
      lastPractitionerButtonPropsRef.current = {
        managerId: null,
        imageUrl: null,
        buttonText: null,
        isActive: null,
        role: null
      }
      return null
    }
    
    // Normalize image URL
    const normalizedImageUrl = selectedManagerImageUrl ? String(selectedManagerImageUrl).trim() : null
    
    // Check if values actually changed
    const propsChanged = 
      lastPractitionerButtonPropsRef.current.managerId !== selectedManagerId ||
      lastPractitionerButtonPropsRef.current.imageUrl !== normalizedImageUrl ||
      lastPractitionerButtonPropsRef.current.buttonText !== practitionerButtonText ||
      lastPractitionerButtonPropsRef.current.isActive !== isSelectedManagerActive ||
      lastPractitionerButtonPropsRef.current.role !== selectedManagerRole
    
    // If props haven't changed, return cached object
    if (!propsChanged && practitionerButtonPropsRef.current !== null) {
      return practitionerButtonPropsRef.current
    }
    
    // Create new object only when values actually changed
    const newProps = {
      imageUrl: normalizedImageUrl,
      managerId: selectedManagerId,
      buttonText: practitionerButtonText,
      isActive: isSelectedManagerActive,
      role: selectedManagerRole,
    }
    
    // Update cache
    lastPractitionerButtonPropsRef.current = {
      managerId: selectedManagerId,
      imageUrl: normalizedImageUrl,
      buttonText: practitionerButtonText,
      isActive: isSelectedManagerActive,
      role: selectedManagerRole
    }
    practitionerButtonPropsRef.current = newProps
    
    return newProps
  }, [
    selectedManagerId,
    selectedManagerImageUrl,
    practitionerButtonText,
    isSelectedManagerActive,
    selectedManagerRole
  ])

  useEffect(() => {
    getAndSetActiveMentorsInAClinicLocation()
    setFlowId(ROLE_CA_FLOWS.FLOW_RECEPTION_TABLET)

    const intervalId = setInterval(() => {
      getAndSetActiveMentorsInAClinicLocation()
    }, 180000) // 3 minutes in milliseconds

    return () => clearInterval(intervalId)
  }, [])

  // Lock body scroll when drawer is open
  useBodyScrollLock(practitionerDrawerOpen, 1300)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <AvailabilityPageContainer>
      <PageHeadlineComponent
        fullWidthHeader
        noPadding
        pageContainerCustomStyles={{
          padding: 0,
          marginBottom: '0',
          position: 'sticky',
          top: '0px',
          zIndex: '15',
          background: 'white',
        }}
        pageHeadlineCustomStyles={{ padding: 0 }}
        pageContainerRef={headerRef}
        heading={
          <HeaderItems
            clinicBrandId={memoizedClinicBrandId}
            clinicId={clinicId}
            clinicAssociationData={memoizedClinicAssociationData}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            mentorProfileData={headerMentorProfileData}
            refreshData={refreshData}
            hideRefreshButton
            hideCheckInButton
            hideSendToDoctorButton
            searchPatientApiSignature="/api/secure/ca/patient/list"
            onOpenPatientSearch={handleOpenPatientSearch}
            hidePatientHistoryRelatedTabs={memoizedHidePatientHistoryRelatedTabs}
            patientHistoryGetApiSignature={patientHistoryApiSignature}
            customExitButton={customExitButton}
            onAssistantNameClick={profileDrawer.open}
            isClinicAssistantFlow={true}
          />
        }
      />

      {/* Practitioner Selection Drawer for Clinic Assistants */}
      <Drawer
        anchor="right"
        open={practitionerDrawerOpen}
        onClose={() => setPractitionerDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '600px', md: '700px' },
            padding: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        ModalProps={{
          disableScrollLock: true, // We handle scroll lock with useBodyScrollLock hook
        }}
      >
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Drawer Header */}
          <Box
            sx={{
              padding: '24px',
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(116, 80, 170, 0.03) 100%)',
              flexShrink: 0,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'rgba(116, 80, 170, 0.9)' }}>
              Select Practitioner
            </Typography>
            <IconButton
              onClick={() => setPractitionerDrawerOpen(false)}
              sx={{
                color: 'rgba(116, 80, 170, 0.7)',
                '&:hover': {
                  backgroundColor: 'rgba(116, 80, 170, 0.1)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Drawer Content - Scrollable */}
          <Box 
            sx={{ 
              flex: 1, 
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '20px',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0, 0, 0, 0.05)',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(116, 80, 170, 0.3)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(116, 80, 170, 0.5)',
                },
              },
            }}
          >
            {loadingObject.managersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Typography>Loading practitioners...</Typography>
              </Box>
            ) : groupedValidManagers().length === 0 ? (
              <Box sx={{ padding: '40px', textAlign: 'center' }}>
                <Typography color="text.secondary">No practitioners available</Typography>
              </Box>
            ) : (
              groupedValidManagers().map((group, groupIndex) => {
                const isDoctorGroup = group.label === 'Doctors'
                const isTherapistGroup = group.label === 'Therapists'
                
                return (
                  <Box key={group.label || `group-${groupIndex}`} sx={{ marginBottom: groupIndex < groupedValidManagers().length - 1 ? '32px' : 0 }}>
                    {/* Section Header */}
                    <Box
                      sx={{
                        padding: '12px 16px',
                        marginBottom: '16px',
                        borderRadius: '8px',
                        background: isDoctorGroup
                          ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)'
                          : isTherapistGroup
                          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)'
                          : 'linear-gradient(135deg, rgba(116, 80, 170, 0.1) 0%, rgba(116, 80, 170, 0.05) 100%)',
                        border: `1px solid ${
                          isDoctorGroup
                            ? 'rgba(59, 130, 246, 0.2)'
                            : isTherapistGroup
                            ? 'rgba(16, 185, 129, 0.2)'
                            : 'rgba(116, 80, 170, 0.2)'
                        }`,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: isDoctorGroup
                            ? 'rgba(59, 130, 246, 0.9)'
                            : isTherapistGroup
                            ? 'rgba(16, 185, 129, 0.9)'
                            : 'rgba(116, 80, 170, 0.9)',
                          fontSize: '16px',
                        }}
                      >
                        {group.label} ({group.options?.length || 0})
                      </Typography>
                    </Box>

                    {/* Cards Grid */}
                    <Grid container spacing={2}>
                      {group.options?.map((manager, managerIndex) => {
                        const isSelected = manager?.id === selectedManagerId
                        const isActive = manager?.isActive
                        const isDoctor = manager?.categories?.includes('doctor')
                        const isTherapist = manager?.categories?.includes('therapist')
                        // Get stable image URL from memoized map - this prevents recalculation on every render
                        const managerImageUrl = managerProfilePicUrls.get(manager?.id) || null

                        return (
                          <Grid item xs={12} sm={6} key={manager?.id || `manager-${group.label}-${managerIndex}`}>
                            <Card
                              onClick={() => {
                                setSelectedManagerId(manager?.id)
                                setPractitionerDrawerOpen(false)
                                setTimeout(() => {
                                  if (refreshData) {
                                    refreshData()
                                  }
                                }, 100)
                              }}
                              sx={{
                                cursor: 'pointer',
                                height: '100%',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                border: isSelected
                                  ? `2px solid ${
                                      isDoctor
                                        ? 'rgba(59, 130, 246, 0.8)'
                                        : isTherapist
                                        ? 'rgba(16, 185, 129, 0.8)'
                                        : 'rgba(116, 80, 170, 0.8)'
                                    }`
                                  : `1px solid ${
                                      isDoctor
                                        ? 'rgba(59, 130, 246, 0.2)'
                                        : isTherapist
                                        ? 'rgba(16, 185, 129, 0.2)'
                                        : 'rgba(116, 80, 170, 0.2)'
                                    }`,
                                background: isSelected
                                  ? isDoctor
                                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.03) 100%)'
                                    : isTherapist
                                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(16, 185, 129, 0.03) 100%)'
                                    : 'linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(116, 80, 170, 0.03) 100%)'
                                  : isActive
                                  ? isDoctor
                                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(59, 130, 246, 0.02) 100%)'
                                    : isTherapist
                                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)'
                                    : 'linear-gradient(135deg, rgba(116, 80, 170, 0.05) 0%, rgba(116, 80, 170, 0.02) 100%)'
                                  : 'white',
                                boxShadow: isSelected
                                  ? `0 4px 12px ${
                                      isDoctor
                                        ? 'rgba(59, 130, 246, 0.25)'
                                        : isTherapist
                                        ? 'rgba(16, 185, 129, 0.25)'
                                        : 'rgba(116, 80, 170, 0.25)'
                                    }`
                                  : isActive
                                  ? `0 2px 8px ${
                                      isDoctor
                                        ? 'rgba(59, 130, 246, 0.15)'
                                        : isTherapist
                                        ? 'rgba(16, 185, 129, 0.15)'
                                        : 'rgba(116, 80, 170, 0.15)'
                                    }`
                                  : '0 1px 3px rgba(0, 0, 0, 0.1)',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 6px 16px ${
                                    isDoctor
                                      ? 'rgba(59, 130, 246, 0.2)'
                                      : isTherapist
                                      ? 'rgba(16, 185, 129, 0.2)'
                                      : 'rgba(116, 80, 170, 0.2)'
                                  }`,
                                },
                                position: 'relative',
                                overflow: 'visible',
                              }}
                            >
                              {isActive && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: '-8px',
                                    right: '12px',
                                    zIndex: 1,
                                  }}
                                >
                                  <Chip
                                    label="Checked In"
                                    size="small"
                                    sx={{
                                      height: '24px',
                                      fontSize: '11px',
                                      fontWeight: 700,
                                      backgroundColor: '#10b981',
                                      color: 'white',
                                      boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                                    }}
                                  />
                                </Box>
                              )}
                              <CardContent sx={{ padding: '20px !important' }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    flexDirection: { xs: 'row', sm: 'row' },
                                  }}
                                >
                                  <Avatar
                                    key={`manager-avatar-${manager?.id}`} // Stable key based only on manager ID to prevent remounting
                                    src={managerImageUrl || undefined}
                                    imgProps={{
                                      onError: (e) => {
                                        // Prevent further error events and infinite retries
                                        e.target.onerror = null
                                        // Remove the src to prevent repeated load attempts
                                        e.target.src = ''
                                        // Set a flag to prevent future loads
                                        if (e.target.parentElement) {
                                          e.target.parentElement.dataset.imageError = 'true'
                                        }
                                      },
                                      onLoad: (e) => {
                                        // Clear error flag on successful load
                                        if (e?.target?.parentElement) {
                                          e.target.parentElement.dataset.imageError = 'false'
                                        }
                                      },
                                      loading: 'lazy',
                                      decoding: 'async',
                                    }}
                                    sx={{
                                      width: { xs: 64, sm: 72 },
                                      height: { xs: 64, sm: 72 },
                                      bgcolor: isDoctor
                                        ? 'rgba(59, 130, 246, 0.1)'
                                        : isTherapist
                                        ? 'rgba(16, 185, 129, 0.1)'
                                        : 'rgba(116, 80, 170, 0.1)',
                                      color: isDoctor
                                        ? 'rgba(59, 130, 246, 0.9)'
                                        : isTherapist
                                        ? 'rgba(16, 185, 129, 0.9)'
                                        : 'rgba(116, 80, 170, 0.9)',
                                      border: `3px solid ${
                                        isSelected
                                          ? isDoctor
                                            ? 'rgba(59, 130, 246, 0.6)'
                                            : isTherapist
                                            ? 'rgba(16, 185, 129, 0.6)'
                                            : 'rgba(116, 80, 170, 0.6)'
                                          : isDoctor
                                          ? 'rgba(59, 130, 246, 0.3)'
                                          : isTherapist
                                          ? 'rgba(16, 185, 129, 0.3)'
                                          : 'rgba(116, 80, 170, 0.3)'
                                      }`,
                                      boxShadow: `0 2px 8px ${
                                        isDoctor
                                          ? 'rgba(59, 130, 246, 0.2)'
                                          : isTherapist
                                          ? 'rgba(16, 185, 129, 0.2)'
                                          : 'rgba(116, 80, 170, 0.2)'
                                      }`,
                                    }}
                                  >
                                    <PersonIcon sx={{ fontSize: { xs: 32, sm: 36 } }} />
                                  </Avatar>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: isSelected ? 700 : 600,
                                        color: isSelected
                                          ? isDoctor
                                            ? 'rgba(59, 130, 246, 0.9)'
                                            : isTherapist
                                            ? 'rgba(16, 185, 129, 0.9)'
                                            : 'rgba(116, 80, 170, 0.9)'
                                          : 'rgba(0, 0, 0, 0.87)',
                                        marginBottom: '4px',
                                        fontSize: { xs: '16px', sm: '18px' },
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {manager?.name || '--'}
                                    </Typography>
                                    <Chip
                                      label={isDoctor ? 'Doctor' : isTherapist ? 'Therapist' : 'Other'}
                                      size="small"
                                      sx={{
                                        height: '22px',
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        backgroundColor: isDoctor
                                          ? 'rgba(59, 130, 246, 0.1)'
                                          : isTherapist
                                          ? 'rgba(16, 185, 129, 0.1)'
                                          : 'rgba(116, 80, 170, 0.1)',
                                        color: isDoctor
                                          ? 'rgba(59, 130, 246, 0.9)'
                                          : isTherapist
                                          ? 'rgba(16, 185, 129, 0.9)'
                                          : 'rgba(116, 80, 170, 0.9)',
                                        border: `1px solid ${
                                          isDoctor
                                            ? 'rgba(59, 130, 246, 0.3)'
                                            : isTherapist
                                            ? 'rgba(16, 185, 129, 0.3)'
                                            : 'rgba(116, 80, 170, 0.3)'
                                        }`,
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        )
                      })}
                    </Grid>
                  </Box>
                )
              })
            )}
          </Box>
        </Box>
      </Drawer>

      {/* <ClinicHeader
        component={
          <div className="d-flex gap-2 justify-content-center align-items-center">
            <GenericHeaderBackButton
              clickfunction={() => window.history.back()}
              styles={{ margin: '20px' }}
            />
            <HeaderGreeting main>{thisClinicData?.name}</HeaderGreeting>
          </div>
        }
      /> */}

      {/* Clinic Assistant Profile Drawer */}
      <ClinicAssistantProfileDrawer
        open={profileDrawer.isOpen}
        onClose={profileDrawer.close}
        clinicId={clinicId}
        assistantData={assistantData}
      />

      <ClinicManageBookingsContainer style={{ marginTop: 0 }}>
        <div>
          {/* <ClinicTabButtonsContainer>
            {validManagers?.map((manager, supportRoleIndex) => (
              <ClinicTabButton
                selected={manager?.id === selectedManagerId}
                style={{ border: '1px solid black', position: 'relative' }}
                key={supportRoleIndex}
                onClick={() => {
                  setSelectedManagerId(manager?.id)
                }}
              >
                {manager?.name}
                {manager?.isActive && (
                  <div
                    style={{
                      position: 'absolute',
                      right: '-8px',
                      top: '-8px',
                      background: 'green',
                      padding: '2px 4px',
                      borderRadius: '4px',
                      color: '#fff',
                      fontSize: '8px',
                    }}
                  >
                    Checked In
                  </div>
                )}
              </ClinicTabButton>
            ))}
          </ClinicTabButtonsContainer> */}
          {!loadingObject.mentorProfileDataLoading &&
            selectedManagersProfileData &&
            thisClinicData &&
            managersProfileData && (
              <>
                {selectedTab === MENTOR_CLINIC_BOOKING_TABS.patients.id ? (
                  <PatientListingPageComponent
                    clinicLocationId={thisClinicData?.id}
                    clinicBrandId={thisClinicData?.clinicBrandId}
                    clinicLocationName={thisClinicData?.name}
                    canEditPatient={true}
                    getPatientListApiSignature="/api/secure/ca/patient/list"
                  />
                ) : (
                  <DayViewCalendarComponent
                    forMentor={false}
                    compactUI
                    hideDropdownForMentorSelection
                    currentView={selectedTab}
                    showPatientHistoryInUploadPrescriptionWidget={
                      assistantProfileForThisClinic?.canViewPatientHistory
                    }
                    // showPatientHistoryInUploadPrescriptionWidget={true}

                    patientSearchApiSignature={`/api/secure/ca/patient/list`}
                    patientHistoryGetApiSignature={`/api/secure/patient/reports-for-clinic-assistant/list?practitioner-id=${selectedManagerId}`}
                    bookingLisitingApiSignature={`/api/secure/clinic-assistant/booked-slots/all?mentorId=${selectedManagerId}`}
                    mentorId={selectedManagerId}
                    mentorProfileData={selectedManagersProfileData}
                    thisClinicData={thisClinicData}
                    publicBookingJoiningLinksApiSignature="/api/secure/clinic-assistant/video-session-tokens/get"
                    mentorClinicAssociationData={assistantData?.associatedClinicsData}
                    allProfessionalsDataForAssistant={managersProfileData}
                    canPersonPrintPrescription={canPersonPrintPrescription}
                    chambersData={assistantData?.associatedChamberRoomsData}
                    assistantProfileInThisClinic={assistantProfileForThisClinic}
                    onOpenPatientSearch={() => setIsPatientSearchOpen(true)}
                    onOpenPractitionerDrawer={handlePractitionerButtonClick}
                    practitionerButtonProps={practitionerButtonProps}
                    ref={dayViewCalendarRef}
                    headerContainerStyles={{
                      position: 'sticky',
                      top: `${dimensions?.height || 0}px`,
                    }}
                    clinicAppointmentTypesList={
                      assistantData?.associatedClinicAppointmentTypesData?.[thisClinicData?.id]
                    }
                    getPrescriptionImagesApiSignature={`/api/secure/clinic-assistant/prescription/images/get-by-eventId`}
                    savePrescriptionImagesApiSignature={`/api/secure/clinic-assistant/prescription/images/save`}
                    fileUploadApiSignature={`/api/secure/clinic-assistant/emr-report/file/upload`}
                    vitalsFormGetApiSignature={(() => {
                      // Detect portal based on pathname
                      if (typeof window !== 'undefined') {
                        const pathname = window.location.pathname
                        if (pathname.startsWith('/mentor-home/') || pathname.startsWith('/mentor/') || pathname.startsWith('/teacher/') || pathname.startsWith('/l/')) {
                          // Mentor/Practitioner Portal - no params needed
                          return '/api/secure/practitioner/vitals/form'
                        }
                        if (pathname.startsWith('/support-home/') || pathname.startsWith('/support/')) {
                          // Support Portal - use practitioner API
                          return '/api/secure/practitioner/vitals/form'
                        }
                      }
                      // Default to clinic-assistant API (with params will be added in DayViewCalendarComponent)
                      return '/api/secure/clinic-assistant/vitals/form'
                    })()}
                    patientUpdateApiSignature="/api/secure/patient/update"
                    patientTagsApiSignature={(() => {
                      // Detect portal based on pathname
                      if (typeof window !== 'undefined') {
                        const pathname = window.location.pathname
                        if (pathname.startsWith('/mentor-home/') || pathname.startsWith('/mentor/') || pathname.startsWith('/teacher/') || pathname.startsWith('/l/')) {
                          // Mentor/Practitioner Portal
                          return '/api/secure/teacher/patient/tags'
                        }
                        if (pathname.startsWith('/support-home/') || pathname.startsWith('/support/')) {
                          // Support Portal
                          return '/api/secure/cc/support/patient/tags'
                        }
                      }
                      // Default to clinic-assistant API
                      return '/api/secure/clinic-assistant/patient/tags'
                    })()}
                    mentorsList={validManagers || []}
                    onChangeMentorProfileData={(profileData) => {
                      setSelectedManagerId(profileData?.id)
                    }}
                    forceShowJoiningDetailsActionButton={
                      assistantProfileForThisClinic?.canViewJoiningDetails
                    }
                    hideRescheduleDialog={!assistantProfileForThisClinic?.canRescheduleBookings}
                  />
                )}
              </>
            )}
        </div>
      </ClinicManageBookingsContainer>

      {/* Patient Search Drawer */}
      {thisClinicData && selectedManagersProfileData && (
        <PatientSearchDrawer
          open={isPatientSearchOpen}
          onClose={() => setIsPatientSearchOpen(false)}
          onSelectPatient={(patient) => {
            setIsPatientSearchOpen(false)
            // You can add additional logic here if needed
          }}
          onBookAppointment={(patient) => {
            if (dayViewCalendarRef.current?.openBookingWithPatient) {
              dayViewCalendarRef.current.openBookingWithPatient(patient)
              setIsPatientSearchOpen(false)
            }
          }}
          clinicId={thisClinicData?.id}
          clinicBrandId={thisClinicData?.clinicBrandId}
          mentorProfileData={selectedManagersProfileData}
          searchPatientApiSignature="/api/secure/ca/patient/list"
          createPatientApiSignature="/api/secure/patient/create"
          canViewPatientHistory={assistantProfileForThisClinic?.canViewPatientHistory}
          patientHistoryGetApiSignature={patientHistoryApiSignature}
        />
      )}
    </AvailabilityPageContainer>
  )
}
